/**
 * De verlanglijstpagina — fase 0.
 *
 * Eén lijst, geen projecten, geen selectie. Prijzen worden nooit bewaard maar
 * bij elk bezoek live opgehaald via de Ajax API (/products/{handle}.js), zodat
 * het totaal ook na een prijsronde klopt en we «prijs gedaald» kunnen tonen.
 */

import { wishlist, WISHLIST_CHANGE } from '@theme/wishlist-store';
import { publishWishlistEvent } from '@theme/wishlist-analytics';
import { CartAddEvent } from '@theme/events';

const CONCURRENCY = 4;
const RETRY_STATUSES = new Set([429, 500, 502, 503, 504]);
const RETRIES = 2;

const escapeHtml = (value) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]
  );

/**
 * Attributen waarmee staal-formaatkiezer.js een staalknop herkent die meerdere formaten
 * heeft (SHO-9835). ?view=wishlist levert die formaten mee. Zijn er geen — het geval bij
 * verreweg de meeste stalen — dan komt er niets bij en blijft de knop wat hij was: één tik
 * die het staal in de mand legt. (Zie templates/product.wishlist.liquid.)
 *
 * @param {{formaten?: unknown[], beeld?: string|null}} sample
 * @param {string} titel Naam van het product waar het staal bij hoort
 * @param {string} labelToevoegen
 */
const sampleFormats = (sample, titel, labelToevoegen) => {
  if (!Array.isArray(sample?.formaten) || sample.formaten.length < 2) return '';
  return (
    ` data-staal-varianten="${escapeHtml(JSON.stringify(sample.formaten))}"` +
    ` data-staal-titel="${escapeHtml(titel)}"` +
    ` data-staal-beeld="${escapeHtml(sample.beeld ?? '')}"` +
    ` data-staal-bron="verlanglijst"` +
    ` data-label-toevoegen="${escapeHtml(labelToevoegen)}"` +
    ` data-label-gekozen="Toegevoegd"`
  );
};

class WishlistPage extends HTMLElement {
  #variants = new Map();
  #loaded = new Set();
  /** handle → gekoppeld staal uit intern.staalproduct. */
  #samples = new Map();
  #strings = {};
  #controller = null;
  #pending = false;
  #viewed = false;
  #failed = false;

  connectedCallback() {
    this.#strings = this.#readStrings();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener(WISHLIST_CHANGE, () => this.#refresh(), { signal });
    this.addEventListener('click', this.#onClick, { signal });

    this.#refresh();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #readStrings() {
    const node = this.querySelector('[data-wishlist-strings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent ?? '{}');
    } catch {
      return {};
    }
  }

  /** Waar de bezoeker vandaan kwam: het header-hartje, de popup, of direct. */
  #openSource() {
    try {
      const source = sessionStorage.getItem('hds:wishlist:source');
      sessionStorage.removeItem('hds:wishlist:source');
      return source || 'direct';
    } catch {
      return 'direct';
    }
  }

  #t(key, replacements = {}) {
    const single = replacements.count === 1;
    let value = (single ? this.#strings[`${key}_one`] : null) ?? this.#strings[key] ?? '';
    for (const [name, replacement] of Object.entries(replacements)) {
      value = value.replaceAll(`{${name}}`, String(replacement));
    }
    return value;
  }

  /* ── Live productdata ──────────────────────────────────────────────── */

  async #refresh() {
    const handles = [...new Set(wishlist.items().map((item) => item.h).filter(Boolean))];
    const missing = handles.filter((handle) => !this.#loaded.has(handle));

    // #pending vóór het eerste render zetten: anders ziet de bezoeker één frame
    // de foutmelding terwijl het laden nog moet beginnen.
    if (missing.length && !this.#pending) {
      this.#pending = true;
      this.#failed = false;
      this.#render();
      await this.#load(missing);
      this.#pending = false;
    }

    this.#render();
  }

  async #load(handles) {
    const queue = [...handles];
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      while (queue.length) {
        const handle = queue.shift();
        if (!handle) return;
        await this.#loadOne(handle);
      }
    });
    await Promise.all(workers);
  }

  /**
   * ?view=wishlist geeft prijs, voorraad ÉN het gekoppelde staal in één verzoek.
   * De Ajax API kan dat laatste niet: die geeft geen metafields.
   *
   * Bij 429 of een 5xx opnieuw proberen met een kleine pauze, en de handle NIET
   * als geladen markeren — anders blijft een product bij een tijdelijke storing
   * de hele sessie onzichtbaar.
   */
  async #loadOne(handle) {
    const root = window.Shopify?.routes?.root ?? '/';

    for (let attempt = 0; attempt <= RETRIES; attempt += 1) {
      try {
        const response = await fetch(`${root}products/${handle}?view=wishlist`);

        if (RETRY_STATUSES.has(response.status) && attempt < RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
          continue;
        }

        if (response.status === 404) {
          this.#loaded.add(handle); // product bestaat niet meer; regel valt weg
          return;
        }
        if (!response.ok) break;

        const product = await response.json();
        this.#loaded.add(handle);
        if (product.staal?.available) this.#samples.set(handle, product.staal);

        for (const variant of product.variants) {
          this.#variants.set(String(variant.id), {
            handle: product.handle,
            title: product.title,
            url: `${product.url}?variant=${variant.id}`,
            image: variant.image ?? null,
            price: variant.price,
            available: variant.available,
            kind: (product.type || '').toLowerCase(),
          });
        }
        return;
      } catch {
        if (attempt >= RETRIES) break;
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
      }
    }

    this.#failed = true; // geen data: dat mag niet als «lege lijst» lezen
  }

  /* ── Rekenen ───────────────────────────────────────────────────────── */

  #rows() {
    return wishlist
      .items()
      .map((item) => ({ item, data: this.#variants.get(String(item.id)) }))
      .filter((row) => row.data);
  }

  #totals(rows) {
    const buyable = rows.filter((row) => row.data.available);
    return {
      buyable,
      cents: buyable.reduce((total, row) => total + row.data.price, 0),
      excluded: rows.length - buyable.length,
    };
  }

  #sized(url, width) {
    if (!url || typeof url !== 'string') return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  #money(cents) {
    const amount = (cents / 100).toLocaleString('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const sample = this.#strings.money_sample;
    if (sample && /\d/.test(sample)) return sample.replace(/[\d.,]+/, amount);
    return (this.#strings.money_format ?? '€ {amount}').replaceAll('{amount}', amount);
  }

  /* ── Weergave ──────────────────────────────────────────────────────── */

  #render() {
    const view = this.querySelector('[data-wishlist-view]');
    if (!view) return;

    const rows = this.#rows();
    const totals = this.#totals(rows);

    this.toggleAttribute('aria-busy', this.#pending);

    view.innerHTML = `
      <div class="wishlist-head wishlist-head--row">
        <div>
          <h1 class="wishlist-title">${escapeHtml(this.#t('title'))}</h1>
          ${
            this.#pending && !rows.length
              ? '<span class="wishlist-skeleton-bone wishlist-skeleton-bone--sub" aria-hidden="true"></span>'
              : `<p class="wishlist-sub">${escapeHtml(this.#t('subtitle', { count: rows.length }))}</p>`
          }
        </div>
        ${rows.length ? `<button class="wishlist-link" type="button" data-clear>${escapeHtml(this.#t('clear'))}</button>` : ''}
      </div>

      ${
        rows.length
          ? `<div class="wishlist-detail">
               <div class="wishlist-items">
                 ${rows.map((row) => this.#card(row)).join('')}
               </div>
               ${this.#summary(rows, totals)}
             </div>`
          : wishlist.count() === 0
            ? `<div class="wishlist-empty">
                 <p><strong>${escapeHtml(this.#t('empty_title'))}</strong></p>
                 <p>${escapeHtml(this.#t('empty_body'))}</p>
               </div>`
            : this.#pending
            ? this.#skeleton(wishlist.count())
            : `<div class="wishlist-empty">
                 <p><strong>${escapeHtml(this.#t('load_error_title'))}</strong></p>
                 <p>${escapeHtml(this.#t('load_error_body', { count: wishlist.count() }))}</p>
                 <p style="margin-block-start:12px">
                   <button class="wishlist-item__sample" style="max-inline-size:14rem;margin-inline:auto" type="button" data-retry>
                     ${escapeHtml(this.#t('retry'))}
                   </button>
                 </p>
               </div>`
      }`;

    // Eén keer per bezoek meten dat de lijst geopend is — óók als hij leeg is of
    // niet kon laden. Alleen meten bij gevulde lijsten zou de vraag «hoeveel
    // mensen zoeken hun lijst op» onbeantwoord laten.
    if (!this.#viewed && !this.#pending) {
      this.#viewed = true;
      publishWishlistEvent('hds:wishlist_view', {
        count: wishlist.count(),
        total: totals.cents,
        state: rows.length ? 'items' : wishlist.count() ? 'error' : 'empty',
        source: this.#openSource(),
      });
    }
  }

  #skeletonCard() {
    return `
      <article class="wishlist-item" aria-hidden="true">
        <div class="wishlist-item__media">
          <span class="wishlist-skeleton-bone"></span>
        </div>
        <div class="wishlist-item__body">
          <span class="wishlist-skeleton-bone wishlist-skeleton-bone--title"></span>
          <span class="wishlist-skeleton-bone wishlist-skeleton-bone--title-short"></span>
          <span class="wishlist-skeleton-bone wishlist-skeleton-bone--price"></span>
          <div class="wishlist-item__actions">
            <span class="wishlist-skeleton-bone wishlist-skeleton-bone--button"></span>
            <span class="wishlist-skeleton-bone wishlist-skeleton-bone--button"></span>
          </div>
        </div>
      </article>`;
  }

  #skeleton(count) {
    const cards = Math.min(Math.max(count, 1), 8);

    return `
      <div class="wishlist-detail">
        <span class="visually-hidden">${escapeHtml(this.#t('loading'))}</span>
        <div class="wishlist-items">
          ${Array.from({ length: cards }, () => this.#skeletonCard()).join('')}
        </div>
        <aside class="wishlist-summary" aria-hidden="true">
          <span class="wishlist-skeleton-bone wishlist-skeleton-bone--label"></span>
          <span class="wishlist-skeleton-bone wishlist-skeleton-bone--amount"></span>
          <span class="wishlist-skeleton-bone wishlist-skeleton-bone--note"></span>
          <span class="wishlist-skeleton-bone wishlist-skeleton-bone--cta"></span>
        </aside>
      </div>`;
  }

  #card({ item, data }) {
    const dropped = item.p0 > data.price ? item.p0 - data.price : 0;
    const sample = this.#samples.get(data.handle);

    return `
      <article class="wishlist-item">
        <div class="wishlist-item__media">
          ${
            data.image
              ? `<a href="${data.url}"><img src="${data.image}" alt="${escapeHtml(data.title)}" loading="lazy" width="300" height="375"></a>`
              : '<span class="wishlist-item__placeholder"></span>'
          }
          <button class="wishlist-item__remove" type="button" data-remove="${item.id}"
            aria-label="${escapeHtml(this.#t('remove_item', { title: data.title }))}">✕</button>
          ${
            dropped || !data.available
              ? `<div class="wishlist-item__badges">
                   ${dropped ? `<span class="wishlist-badge wishlist-badge--drop">${escapeHtml(this.#t('dropped', { amount: this.#money(dropped) }))}</span>` : ''}
                   ${data.available ? '' : `<span class="wishlist-badge wishlist-badge--oos">${escapeHtml(this.#t('unavailable'))}</span>`}
                 </div>`
              : ''
          }
        </div>
        <div class="wishlist-item__body">
          <h3 class="wishlist-item__heading">
            <a class="wishlist-item__title" href="${data.url}">${escapeHtml(data.title)}</a>
          </h3>
          <span class="wishlist-item__price">${this.#money(data.price)}</span>
          <div class="wishlist-item__actions">
            ${
              // Beide slots staan er altijd, ook leeg. Zonder die reservering
              // wordt een kaart zonder staal (of zonder voorraad) korter en
              // lijnen de knoppen in een rij niet meer uit.
              data.available
                ? `<button class="wishlist-item__cart" type="button" data-cart-item="${item.id}">
                     <span class="wishlist-label-long">${escapeHtml(this.#t('cart_item'))}</span>
                     <span class="wishlist-label-short">${escapeHtml(this.#t('cart_item_short'))}</span>
                   </button>`
                : '<span class="wishlist-item__slot" aria-hidden="true"></span>'
            }
            ${
              sample
                ? `<button class="wishlist-item__sample" type="button" data-cart-sample="${sample.variant_id}"${sampleFormats(sample, data.title, this.#t('sample_add'))}>
                     <span aria-hidden="true">+</span> <span data-staal-knop-label>${escapeHtml(this.#t('sample_add'))}</span>
                   </button>`
                : '<span class="wishlist-item__slot" aria-hidden="true"></span>'
            }
          </div>
        </div>
      </article>`;
  }

  #summary(rows, totals) {
    return `
      <aside class="wishlist-summary">
        <p class="wishlist-summary__label">${escapeHtml(this.#t('total_label'))}</p>
        <p class="wishlist-summary__amount">${this.#money(totals.cents)}</p>
        <div class="wishlist-summary__notes">
          <span>${escapeHtml(this.#t('counted_count', { count: totals.buyable.length, total: rows.length }))}</span>
          ${totals.excluded ? `<span>${escapeHtml(this.#t('excluded_note', { count: totals.excluded }))}</span>` : ''}
        </div>
        <div class="wishlist-summary__actions">
          <button class="button" type="button" data-cart ${totals.buyable.length ? '' : 'disabled'}>
            ${escapeHtml(this.#t('cart_all', { count: totals.buyable.length }))}
          </button>
        </div>
      </aside>`;
  }

  /* ── Handelingen ───────────────────────────────────────────────────── */

  #onClick = (event) => {
    const remove = event.target.closest('[data-remove]');
    if (remove) {
      wishlist.remove(remove.dataset.remove);
      return;
    }
    if (event.target.closest('[data-clear]')) {
      wishlist.clear();
      return;
    }
    if (event.target.closest('[data-retry]')) {
      this.#refresh();
      return;
    }

    const one = event.target.closest('[data-cart-item]');
    if (one) {
      this.#addToCart(one, [{ id: Number(one.dataset.cartItem), quantity: 1 }], 'product');
      return;
    }

    const sample = event.target.closest('[data-cart-sample]');
    if (sample) {
      this.#addToCart(
        sample,
        [{ id: Number(sample.dataset.cartSample), quantity: 1, properties: { _bron: 'verlanglijst-staal' } }],
        'staal'
      );
      return;
    }

    const all = event.target.closest('[data-cart]');
    if (all) {
      const items = this.#totals(this.#rows()).buyable.map((row) => ({ id: row.item.id, quantity: 1 }));
      this.#addToCart(all, items, 'alles');
      return;
    }
  };

  /**
   * Eén weg naar de winkelwagen voor alle drie de knoppen (regel, staal, alles),
   * zodat de melding, de meting en het openen van de cart drawer overal gelijk zijn.
   */
  async #addToCart(button, items, kind) {
    if (!items.length) return;

    const label = button.innerHTML;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      const cartUrl = (typeof Theme !== 'undefined' ? Theme : globalThis.Theme)?.routes?.cart_add_url;
      const response = await fetch(cartUrl ?? '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error(String(response.status));

      const data = await response.json();
      publishWishlistEvent('hds:wishlist_cart', { items: items.length, kind });
      this.dispatchEvent(
        new CartAddEvent(data, 'wishlist-page', { source: 'wishlist-page', itemCount: items.length })
      );

      // Korte bevestiging op de knop zelf; de cart drawer doet de rest.
      button.innerHTML = escapeHtml(this.#t('cart_added'));
      setTimeout(() => {
        button.innerHTML = label;
        button.disabled = false;
      }, 2500);
      button.removeAttribute('aria-busy');
      return;
    } catch {
      button.innerHTML = escapeHtml(this.#t('cart_error'));
      setTimeout(() => {
        button.innerHTML = label;
      }, 3000);
    }

    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
